import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createAdminCounterOrder,
  getEtaPreview,
  getPublicMenu,
  validatePromoCode,
  formatApiDetail,
} from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { notifyOrdersChanged } from '../lib/orderSync';

import { buildCartLine, cartTotals, isPizzaItem, lineCartKey } from '../lib/pizzaPricing';
import { formatLineLabel } from '../lib/cartUtils';

export default function CounterOrderDialog({ open, onOpenChange, onCreated }) {
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('store_cash');
  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eta, setEta] = useState(null);

  const resetForm = useCallback(() => {
    setCart([]);
    setName('');
    setPhone('');
    setNotes('');
    setPaymentMethod('store_cash');
    setPromoInput('');
    setPromoApplied(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoadingMenu(true);
    Promise.all([getPublicMenu(), getEtaPreview()])
      .then(([menu, etaData]) => {
        setCategories(menu.categories || []);
        setEta(etaData);
      })
      .catch(() => toast.error('No se pudo cargar el menú'))
      .finally(() => setLoadingMenu(false));
  }, [open]);

  const addItem = (item) => {
    if (!item?.id || item.is_active === false) return;
    const line = buildCartLine({
      item,
      sizeKey: isPizzaItem(item) ? 'mediana' : '',
      qty: 1,
    });
    setCart((prev) => {
      const key = lineCartKey(line);
      const found = prev.find((c) => lineCartKey(c) === key);
      if (found) {
        return prev.map((c) =>
          lineCartKey(c) === key
            ? { ...c, qty: c.qty + 1, line_total: (c.qty + 1) * c.unit_price }
            : c,
        );
      }
      return [...prev, line];
    });
  };

  const changeQty = (key, delta) => {
    setCart((prev) =>
      prev
        .map((c) => {
          if (lineCartKey(c) !== key) return c;
          const qty = Math.max(0, c.qty + delta);
          return { ...c, qty, line_total: qty * c.unit_price };
        })
        .filter((c) => c.qty > 0),
    );
  };

  const totals = useMemo(() => cartTotals(cart, 0), [cart]);
  const subtotal = totals.subtotal;
  const autoDiscount = totals.auto_discount;
  const promoDiscount =
    autoDiscount > 0 ? 0 : promoApplied ? Number(promoApplied.discount || 0) : 0;
  const total = Math.max(0, subtotal - autoDiscount - promoDiscount);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    if (autoDiscount > 0) {
      toast.message('Ya aplica la promo de carta (2x1 / 30%). El cupón no se suma.');
      return;
    }
    setPromoLoading(true);
    try {
      const res = await validatePromoCode({
        code: promoInput.trim(),
        customer_phone: phone.trim(),
        delivery_type: 'pickup',
        lines: cart.map((c) => ({
          menu_item_id: c.menu_item_id,
          qty: c.qty,
          notes: c.notes || '',
          size_key: c.size_key || '',
          half_specialty_id: c.half_specialty_id || '',
          extra_topping_ids: c.extra_topping_ids || [],
        })),
      });
      setPromoApplied(res);
      toast.success('Cupón aplicado');
    } catch (err) {
      setPromoApplied(null);
      toast.error(formatApiDetail(err?.response?.data?.detail, 'Cupón no válido'));
    } finally {
      setPromoLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Indica el nombre del cliente');
      return;
    }
    if (!cart.length) {
      toast.error('Agrega al menos un producto');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (paymentMethod === 'transfer' && phoneDigits.length < 10) {
      toast.error('Indica el teléfono WhatsApp para enviar datos de transferencia');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createAdminCounterOrder({
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        lines: cart.map((c) => ({
          menu_item_id: c.menu_item_id,
          qty: c.qty,
          notes: c.notes || '',
          size_key: c.size_key || '',
          half_specialty_id: c.half_specialty_id || '',
          extra_topping_ids: c.extra_topping_ids || [],
        })),
        payment_method: paymentMethod,
        notes: notes.trim(),
        promo_code: autoDiscount > 0 ? '' : promoApplied?.code || '',
      });
      const order = res?.order || res;
      const onum = order?.order_number || '';
      notifyOrdersChanged();
      if (paymentMethod === 'transfer') {
        if (res?.whatsapp_sent) {
          toast.success(`Pedido en sucursal #${onum} creado. Datos de transferencia enviados por WhatsApp.`);
        } else {
          toast.error(
            `Pedido #${onum} creado, pero NO se envió WhatsApp. ${res?.whatsapp_detail || 'Revisa puente WA o usa «Enviar datos WA» en la bandeja.'}`,
            { duration: 12000 },
          );
        }
      } else if (order?.status === 'awaiting_extras') {
        toast.success(
          `Pedido #${onum} en caja: confirma complementos/bebidas en la bandeja (sin pasar por cocina).`,
        );
      } else if (order?.status === 'ready') {
        toast.success(`Pedido #${onum} listo en caja (solo bebidas/complementos).`);
      } else {
        toast.success(`Pedido en sucursal #${onum} creado y enviado a cocina`);
      }
      resetForm();
      onOpenChange(false);
      onCreated?.(order);
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo crear el pedido'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pedido en sucursal</DialogTitle>
          <DialogDescription>
            Recoger en sucursal. Efectivo pagado en mostrador va directo a cocina; transferencia queda en
            bandeja hasta confirmar comprobante.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="counter-name">Nombre del cliente</Label>
              <Input
                id="counter-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="counter-phone">
                Teléfono (WhatsApp){paymentMethod === 'transfer' ? ' *' : ''}
              </Label>
              <Input
                id="counter-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="10 dígitos"
              />
            </div>
          </div>
          <div>
            <Label>Forma de pago</Label>
            <select
              className="w-full border rounded-md px-3 py-2 mt-1"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="store_cash">Efectivo en sucursal (ya pagado)</option>
              <option value="transfer">Transferencia</option>
            </select>
            {paymentMethod === 'transfer' ? (
              <p className="text-xs text-muted-foreground mt-1">
                Se enviará por WhatsApp: datos bancarios y referencia PEDIDO # (al crear el pedido).
              </p>
            ) : null}
          </div>
          {loadingMenu ? (
            <p className="text-sm text-muted-foreground">Cargando menú…</p>
          ) : (
            <div className="space-y-3 max-h-48 overflow-y-auto border rounded-md p-3">
              {categories.map((cat) => (
                <div key={cat.id}>
                  <p className="text-sm font-semibold mb-1">{cat.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {(cat.items || [])
                      .filter((it) => it.is_active !== false)
                      .map((it) => (
                        <Button
                          key={it.id}
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => addItem(it)}
                        >
                          + {it.name}
                            {isPizzaItem(it)
                              ? ` (desde $${Number(it.sizes?.[0]?.price ?? it.price).toFixed(0)})`
                              : ` ($${Number(it.price).toFixed(0)})`}
                        </Button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {cart.length > 0 ? (
            <div className="text-sm border rounded-md p-2 space-y-2">
              {cart.map((c) => {
                const key = lineCartKey(c);
                return (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate">
                      {formatLineLabel(c)} — ${c.line_total.toFixed(0)}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => changeQty(key, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center tabular-nums">{c.qty}</span>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => changeQty(key, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <p className="font-medium pt-1 border-t">
                Total: ${total.toFixed(0)} MXN
                {autoDiscount > 0 ? (
                  <span className="text-emerald-700 font-normal text-xs ml-1">
                    (−${autoDiscount.toFixed(0)} {totals.auto_discount_detail})
                  </span>
                ) : null}
                {promoDiscount > 0 ? (
                  <span className="text-muted-foreground font-normal text-xs ml-1">
                    (cupón −${promoDiscount.toFixed(0)})
                  </span>
                ) : null}
              </p>
            </div>
          ) : null}
          {eta?.eta_minutes ? (
            <p className="text-xs text-muted-foreground">
              Tiempo estimado: ~{eta.eta_minutes} min
            </p>
          ) : null}
          <div className="flex gap-2">
            <Input
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Cupón"
              className="font-mono"
            />
            <Button type="button" variant="outline" disabled={promoLoading || !promoInput.trim()} onClick={applyPromo}>
              Aplicar
            </Button>
          </div>
          <div>
            <Label htmlFor="counter-notes">Notas (opcional)</Label>
            <Textarea id="counter-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || !cart.length}>
              {submitting ? 'Creando…' : 'Crear pedido en sucursal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
